import { DateOfBirthInput } from '_atoms';
import { HelperMethods } from '_utils';
import React, { useState } from 'react';
import { MdAdd, MdDelete, MdPerson, MdWarning } from 'react-icons/md';

import { ParticipantInfo } from '@types';

interface ParticipantsInfoProps {
  participantsInfo?: ParticipantInfo[];
  onNext: (data?: any) => void;
  onPrevious: () => void;
  onStepComplete: (data: any) => void;
  maxAge?: number; // Optional maximum age limit for participants
  minAge?: number; // Optional minimum age limit for participants
  hideAddButton?: boolean; // Optional flag to hide "Add Another Participant" button
}

export const ParticipantsInfo: React.FC<ParticipantsInfoProps> = ({
  participantsInfo = [],
  onNext,
  onPrevious,
  onStepComplete,
  maxAge,
  minAge,
  hideAddButton = false,
}) => {
  // Validation function for maximum age
  const validateMaxAge = (dateOfBirth: string): boolean => {
    if (!maxAge || !dateOfBirth) return true;
    if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) return false;
    const age = HelperMethods.calculateAge(dateOfBirth);
    return age <= maxAge;
  };

  // Validation function for minimum age
  const validateMinAge = (dateOfBirth: string): boolean => {
    if (!minAge || !dateOfBirth) return true;
    if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) return false;
    const age = HelperMethods.calculateAge(dateOfBirth);
    return age >= minAge;
  };

  const getAgeErrorMessage = (dateOfBirth: string): string | null => {
    if (!dateOfBirth) return null;

    const plausibleError = HelperMethods.getDateOfBirthError(dateOfBirth);
    if (plausibleError) return plausibleError;

    const age = HelperMethods.calculateAge(dateOfBirth);

    if (maxAge && age > maxAge) {
      return `Age must be ${maxAge} years or younger. Current age: ${age}`;
    }

    if (minAge && age < minAge) {
      return `Age must be ${minAge} years or older. Current age: ${age}`;
    }

    return null;
  };
  const [participants, setParticipants] = useState<ParticipantInfo[]>(
    participantsInfo.length > 0
      ? participantsInfo
      : [
          {
            id: '1',
            firstName: '',
            lastName: '',
            dateOfBirth: '',
          },
        ],
  );

  const addParticipant = () => {
    const newId = (participants.length + 1).toString();
    setParticipants([
      ...participants,
      {
        id: newId,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
      },
    ]);
  };

  const removeParticipant = (id: string) => {
    if (participants.length > 1) {
      setParticipants(
        participants.filter(participant => participant.id !== id),
      );
    }
  };

  const updateParticipant = (
    id: string,
    field: keyof ParticipantInfo,
    value: string,
  ) => {
    setParticipants(
      participants.map(participant => {
        if (participant.id === id) {
          return { ...participant, [field]: value };
        }
        return participant;
      }),
    );
  };

  const isParticipantValid = (participant: ParticipantInfo): boolean => {
    const hasRequiredFields =
      !!participant.firstName &&
      !!participant.lastName &&
      !!participant.dateOfBirth;
    const hasPlausibleDob = HelperMethods.isPlausibleDateOfBirth(
      participant.dateOfBirth,
    );
    const hasValidMaxAge = validateMaxAge(participant.dateOfBirth);
    const hasValidMinAge = validateMinAge(participant.dateOfBirth);
    return (
      hasRequiredFields && hasPlausibleDob && hasValidMaxAge && hasValidMinAge
    );
  };

  const handleContinue = () => {
    const isValid = participants.every(isParticipantValid);

    if (isValid) {
      onStepComplete({ participantsInfo: participants });
      onNext({ participantsInfo: participants });
    }
  };

  const isFormValid = participants.every(isParticipantValid);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Participant Information
        </h2>
        <p className="text-gray-600">Provide details for each participant</p>
        {(maxAge || minAge) && (
          <div className="mt-3 inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-800">
              Age requirement: Participants must be{' '}
              {minAge && maxAge
                ? `between ${minAge} and ${maxAge} years old`
                : minAge
                  ? `${minAge} years or older`
                  : `${maxAge} years or younger`}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {participants.map((participant, index) => (
          <div
            key={participant.id}
            className="border border-gray-200 rounded-lg p-2 md:p-6 bg-gray-50"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MdPerson className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Participant {index + 1}
                </h3>
              </div>
              {participants.length > 1 && (
                <button
                  onClick={() => removeParticipant(participant.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <MdDelete className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={participant.firstName}
                  onChange={e =>
                    updateParticipant(
                      participant.id,
                      'firstName',
                      e.target.value,
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !participant.firstName
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={participant.lastName}
                  onChange={e =>
                    updateParticipant(
                      participant.id,
                      'lastName',
                      e.target.value,
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !participant.lastName
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <DateOfBirthInput
                  value={participant.dateOfBirth}
                  onChange={value => {
                    updateParticipant(participant.id, 'dateOfBirth', value);
                  }}
                  min={HelperMethods.getMinDateOfBirth()}
                  max={HelperMethods.getMaxDateOfBirth()}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !participant.dateOfBirth ||
                    getAgeErrorMessage(participant.dateOfBirth)
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  required
                />
                {participant.dateOfBirth &&
                  !getAgeErrorMessage(participant.dateOfBirth) && (
                    <div className="mt-1 text-sm text-gray-600">
                      Age: {HelperMethods.calculateAge(participant.dateOfBirth)}{' '}
                      years old
                    </div>
                  )}
                {getAgeErrorMessage(participant.dateOfBirth) && (
                  <div className="mt-1 text-sm text-red-600 flex items-center">
                    <MdWarning className="mr-1" />
                    {getAgeErrorMessage(participant.dateOfBirth)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Participant Button */}
      {!hideAddButton && (
        <div className="flex justify-center">
          <button
            onClick={addParticipant}
            className="flex items-center justify-center space-x-2 px-6 py-3 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors w-full sm:w-auto min-h-[44px]"
          >
            <MdAdd className="w-5 h-5" />
            <span>Add Another Participant</span>
          </button>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between pt-6 gap-4">
        <button
          onClick={onPrevious}
          className="px-6 py-3 text-gray-700 bg-gray-200 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors w-full sm:w-auto min-h-[44px] order-2 sm:order-1"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!isFormValid}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto min-h-[44px] order-1 sm:order-2"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
